/**
 * Hold / Alternate — the rotation order, as a step row like Schedule's:
 * dragged by hand (the pool's order) or by the gateway formula (best first;
 * one a rule excludes keeps its dragged place after the ranked). The line
 * under the choice shows today's rotation either way, and (Phase 19) the
 * gateways a rule set aside or took out.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ListOrdered } from 'lucide-react';

import type { DraftGateway } from '../../../../drop-generator.types';
import { ChoiceChips } from '../../general/controls/ChoiceChips';
import { StepRow, ValueLine } from '../../general/controls/StepRow';
import type { GatewayOp } from '../../state/gateway-draft';
import { ORDER_HINT } from '../cards/strategy-copy';
import type { RotationView } from '../ranking/formula-pool';
import { GatewayChip } from './GatewayChip';
import { ManualRotation } from './ManualRotation';
import { RuleOutcomeLine } from './RuleOutcomeLine';

const OPTIONS = [
  { value: 'MANUAL', label: 'Drag manually' },
  { value: 'FORMULA', label: 'Formula ranking' },
] as const;
type OrderMode = (typeof OPTIONS)[number]['value'];

const NOTE = 'text-xs text-muted-foreground';

export const OrderZone: FC<{ gateway: DraftGateway; view: RotationView; ranking: boolean; onOp: (op: GatewayOp) => void }> = ({ gateway, view, ranking, onOp }) => (
  <StepRow icon={ListOrdered} label="Order" hint={ORDER_HINT}>
    <ChoiceChips options={OPTIONS} value={gateway.orderMode as OrderMode} onChange={(orderMode) => onOp({ kind: 'patch', patch: { orderMode } })} ariaLabel="Rotation order" />
    <ValueLine key={gateway.orderMode}>
      {gateway.pool.length === 0 ? (
        <span className={NOTE}>No gateways in the pool yet — every drop plans without a route (—).</span>
      ) : gateway.orderMode === 'MANUAL' ? (
        <div className="space-y-2">
          <span className={NOTE}>Today's rotation:</span>
          <ManualRotation gateway={gateway} order={view.order} onOp={onOp} />
        </div>
      ) : (
        <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {ranking ? 'Ranked rotation:' : "Today's rotation:"}
          {view.order.map((poolIndex, i) => (
            <span key={gateway.pool[poolIndex].id} className="flex items-center gap-1.5">
              {i > 0 ? <span aria-hidden>→</span> : null}
              <GatewayChip index={poolIndex} />
            </span>
          ))}
        </span>
      )}
    </ValueLine>
    <RuleOutcomeLine gateway={gateway} view={view} />
    <p className={NOTE}>
      {gateway.orderMode === 'FORMULA'
        ? 'Best score first. A gateway a rule excludes (“Not enough data”) keeps its dragged place after the ranked ones.'
        : 'Drag these gateways or the pool rows to change the rotation order.'}
    </p>
  </StepRow>
);
